<template>
  <v-dialog v-model="dialog" width="640" :fullscreen="$vuetify.breakpoint.smAndDown">
    <v-card>
      <v-card-title class="primary white--text">
        <span>{{ editing ? 'SỬA BỮA ĂN' : 'THÊM BỮA ĂN' }}</span>
        <v-spacer />
        <v-icon color="white" @click="dialog = false">close</v-icon>
      </v-card-title>
      <v-form ref="form" class="pa-4" @keydown.native.enter.prevent="save">
        <v-row>
          <v-col cols="12" md="8">
            <v-text-field v-model="form.title" label="Tên bữa" outlined dense :rules="[$rules.required]" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.timeOfDay"
              :items="timeOfDayOptions"
              label="Thời điểm"
              outlined
              dense
              :rules="[$rules.required]"
            />
          </v-col>

          <template v-if="type === 'fixed'">
            <v-col cols="12" :md="serveRange === 'specific' ? 3 : 6">
              <v-select
                v-model="serveRange"
                :items="serveRangeOptions"
                label="Ngày phục vụ"
                outlined
                dense
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col v-if="serveRange === 'specific'" cols="12" md="3">
              <v-select
                v-model="serveWeekday"
                :items="weekdayOptions"
                label="Thứ"
                outlined
                dense
                :rules="[$rules.required]"
              />
            </v-col>
          </template>
          <v-col v-else cols="12" md="6">
            <DateIOSPicker label="Ngày phục vụ" outlined dense :date.sync="form.serveDate" />
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              v-model="form.menuCount"
              :items="[{ value: 1, text: '1 menu' }, { value: 2, text: '2 menu' }]"
              label="Số menu"
              outlined
              dense
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="form.maxPortion"
              type="number"
              min="1"
              label="Số suất tối đa / HS"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="form.displayOrder"
              type="number"
              min="1"
              label="Thứ tự hiển thị"
              outlined
              dense
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-select v-model="form.feeType" :items="feeTypeOptions" label="Biểu phí" outlined dense />
          </v-col>
          <v-col v-if="form.feeType === 'paid'" cols="12" md="6">
            <v-text-field
              v-model.number="form.feeAmount"
              type="number"
              min="0"
              label="Số tiền / suất"
              outlined
              dense
            />
          </v-col>
        </v-row>
      </v-form>
      <v-alert dense text type="info" class="caption mx-4 mb-0">{{ effectNote }}</v-alert>
      <v-card-actions>
        <v-btn outlined @click="dialog = false">Hủy</v-btn>
        <v-spacer />
        <v-btn color="primary" depressed :loading="saving" @click="save">Lưu</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DateIOSPicker from '@/components/basic/picker/DateIOSPicker.vue'
import { mapActions } from 'vuex'
import moment from 'moment'
import {
  TIME_OF_DAY_OPTIONS,
  SERVE_RANGE_OPTIONS,
  WEEKDAY_OPTIONS,
  FEE_TYPE_OPTIONS,
  EFFECT_NOTE,
  isWeekday
} from '@/modules/mealConfig/mealConfigConstants'

const emptyForm = () => ({
  id: null,
  title: '',
  timeOfDay: 'lunch',
  serveDays: 'mon-sat',
  serveDate: null,
  menuCount: 1,
  maxPortion: 1,
  displayOrder: 1,
  feeType: 'paid',
  feeAmount: 1
})

export default {
  components: { DateIOSPicker },
  props: {
    type: { type: String, default: 'fixed' }
  },
  data() {
    return {
      dialog: false,
      editing: false,
      saving: false,
      form: emptyForm(),
      serveRange: 'mon-sat',
      serveWeekday: 'mon',
      timeOfDayOptions: TIME_OF_DAY_OPTIONS,
      serveRangeOptions: SERVE_RANGE_OPTIONS,
      weekdayOptions: WEEKDAY_OPTIONS,
      feeTypeOptions: FEE_TYPE_OPTIONS,
      effectNote: EFFECT_NOTE
    }
  },
  methods: {
    ...mapActions('mealSlot', ['create', 'update']),
    openCreate() {
      this.editing = false
      this.form = emptyForm()
      this.serveRange = 'mon-sat'
      this.serveWeekday = 'mon'
      if (this.type === 'supplementary') {
        this.form.serveDays = null
        this.form.serveDate = moment().add(1, 'day').format('YYYY-MM-DD')
      }
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openEdit(item) {
      this.editing = true
      this.form = {
        id: item.id,
        title: item.title,
        timeOfDay: item.timeOfDay,
        serveDays: item.serveDays,
        serveDate: item.serveDate ? moment(item.serveDate).format('YYYY-MM-DD') : null,
        menuCount: item.menuCount,
        maxPortion: item.maxPortion,
        displayOrder: item.displayOrder,
        feeType: item.feeType,
        feeAmount: item.feeAmount
      }
      // Tách serveDays ra range / weekday cụ thể
      if (isWeekday(item.serveDays)) {
        this.serveRange = 'specific'
        this.serveWeekday = item.serveDays
      } else {
        this.serveRange = item.serveDays || 'mon-sat'
        this.serveWeekday = 'mon'
      }
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    async save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      try {
        const payload = {
          title: this.form.title.trim(),
          timeOfDay: this.form.timeOfDay,
          type: this.type,
          menuCount: this.form.menuCount,
          maxPortion: this.form.maxPortion,
          displayOrder: this.form.displayOrder,
          feeType: this.form.feeType,
          feeAmount: this.form.feeType === 'free' ? 0 : this.form.feeAmount
        }
        if (this.type === 'fixed') {
          payload.serveDays = this.serveRange === 'specific' ? this.serveWeekday : this.serveRange
        } else {
          payload.serveDate = this.form.serveDate
        }
        let result
        if (this.editing) {
          result = await this.update({ id: this.form.id, ...payload })
          this.$alert.updateSuccess()
        } else {
          result = await this.create(payload)
          this.$alert.addSuccess()
        }
        this.dialog = false
        this.$emit('saved', result)
      } catch (err) {
        this.$alert.error('Lưu bữa ăn thất bại: ' + (err.response ? err.response.data.message : err))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
